import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
  CircularProgress,
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { BackupConfiguracao, backupsApi } from '../../api/backups'
import dayjs from 'dayjs'

interface ExecucoesDialogProps {
  open: boolean
  backup: BackupConfiguracao
  onClose: () => void
}

const ExecucoesDialog: React.FC<ExecucoesDialogProps> = ({ open, backup, onClose }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['backup-execucoes', backup.id],
    queryFn: () => backupsApi.listExecucoesPorBackup(backup.id, { page_size: 50 }),
    enabled: open,
  })

  const execucoes = data?.execucoes || []

  const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'warning' | 'error' => {
    switch (status) {
      case 'concluido':
        return 'success'
      case 'em_progresso':
        return 'primary'
      case 'falhou':
        return 'error'
      case 'pendente':
        return 'warning'
      default:
        return 'default'
    }
  }

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-'
    try {
      return dayjs(dateString).format('DD/MM/YYYY HH:mm:ss')
    } catch {
      return dateString
    }
  }

  const formatDuration = (seconds: number | null): string => {
    if (!seconds) return '-'
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${minutes}m ${secs}s`
  }

  const formatSize = (bytes: number | null): string => {
    if (!bytes) return '-'
    const mb = bytes / (1024 * 1024)
    return `${mb.toFixed(2)} MB`
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Histórico de Execuções - {backup.nome_configuracao}</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : execucoes.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>Nenhuma execução encontrada</Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Data/Hora</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Tamanho</TableCell>
                  <TableCell>Duração</TableCell>
                  <TableCell>Erro</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {execucoes.map((execucao) => (
                  <TableRow key={execucao.id}>
                    <TableCell>{formatDate(execucao.started_at)}</TableCell>
                    <TableCell>
                      <Chip
                        label={execucao.status}
                        size="small"
                        color={getStatusColor(execucao.status)}
                      />
                    </TableCell>
                    <TableCell>
                      {execucao.tipo_backup === 'completo' ? 'Completo' : 'Incremental'}
                    </TableCell>
                    <TableCell>{formatSize(execucao.size_bytes)}</TableCell>
                    <TableCell>{formatDuration(execucao.duration_seconds)}</TableCell>
                    <TableCell>
                      {execucao.error_message ? (
                        <Box
                          sx={{
                            maxWidth: 300,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={execucao.error_message}
                        >
                          {execucao.error_message}
                        </Box>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  )
}

export default ExecucoesDialog

